import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { EmptyState as ErrorMessage } from '@m/alchemy-ui/esm/empty-state/EmptyState';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { BarChart } from '@m/alchemy-ui/BarChart';
import { Select } from '@m/alchemy-ui/Select';
import { useIntl } from 'react-intl';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import {
  type ChallengesQuery,
  type ChallengeWithFiltersQuery,
  type FilteredUsersQuery,
  GET_CHALLENGE_WITH_FILTERS,
  GET_CHALLENGES,
  GET_COUNTRIES,
  GET_IDEAS_AND_COUNT,
  GET_USERFIELDS_FILTERED_USERIDS,
  type GetCountriesQuery,
  type GetIdeasAndCountQuery
} from '../../graphql/queries';
import { useUserFields, useWindowWidth } from '../../hooks';
import { CommunityContext } from '../../context';
import { ChallengeType } from '../../common/enums';
import { ChallengePublicationStatus } from '../../common/core-api';
import {
  ChartActionsRow,
  ChartScroll,
  ChartScrollInner,
  Grid,
  GridCol,
  GridHeader,
  GridRow,
  Pagination
} from './Analytics.styled';
import * as Styled from './components/SearchFilters.styled';
import { CustomFilters } from './CustomFilters';
import { type UserFieldType } from './types';
import { ChartExportMenu, IdeasTable, TableView } from './components';
import { AlchemySortDirectionType, ChallengeSelectionType, CoreApiSortDirectionType } from './enums';
import { EXPORT_TO_CSV_ENDPOINT, IDEAS_DEFAULT_SORT, IDEAS_PER_PAGE } from './constants';
import { accessibleChartTheme, getChartMinWidth, getChartScrollWidth } from './helpers/chartUtils';

export const IdeasByPhaseStats = ({ dataTableViewEnabled }: { dataTableViewEnabled: boolean }) => {
  const intl = useIntl();
  const community = useContext(CommunityContext);
  const { data: communityUserFields } = useUserFields();

  const defaultUserFieldOptions: UserFieldType = {};
  const [userFieldOptions, setUserFieldOptions] = useState(defaultUserFieldOptions);
  const [countries, setCountries] = useState<string[]>([]);
  const [challenge, setChallenge] = useState<string>(ChallengeSelectionType.SELECT);
  const [sort, setSort] = useState<string[][]>([IDEAS_DEFAULT_SORT]);
  const [offset, setOffset] = useState<number>(0);
  const [chartData, setChartData] = useState<ChallengeWithFiltersQuery>();
  const [skip, setSkip] = useState(true);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data: countriesList } = useQuery<GetCountriesQuery>(GET_COUNTRIES, {
    variables: { id: community.id },
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    if (countriesList?.community) {
      countriesList.community && setCountries(countriesList.community.countries as string[]);
    }
  }, [countriesList]);

  const [phase, setPhase] = useState<number>();
  const userFields = JSON.stringify(userFieldOptions);
  const [getChallenge, { data: challengeData }] = useLazyQuery<ChallengeWithFiltersQuery>(GET_CHALLENGE_WITH_FILTERS, {
    variables: { id: challenge, filters: { userFields } }
  });

  useEffect(() => {
    if (challengeData) {
      setChartData(challengeData);
    }
  }, [setChartData, challengeData]);

  const [getFilteredUserIds, { data: customFiltersUserIds }] = useLazyQuery<FilteredUsersQuery>(
    GET_USERFIELDS_FILTERED_USERIDS,
    {
      variables: { filters: { userFields } }
    }
  );

  const challengesVariables = {
    order: ['title'],
    types: [ChallengeType.FUNNEL],
    publicationStatus: ChallengePublicationStatus.PUBLISHED
  };
  const { loading: loadingFunnelChallenges, data: funnelChallengeList } = useQuery<ChallengesQuery>(GET_CHALLENGES, {
    variables: challengesVariables
  });

  const [ideasQueryVariables, setIdeasQueryVariables] = useState({
    challengeSelection: challenge,
    phaseSelection: phase,
    filters: { userFields },
    order: sort,
    offset: offset * 10
  });

  const { data: ideasData } = useQuery<GetIdeasAndCountQuery>(GET_IDEAS_AND_COUNT, {
    variables: ideasQueryVariables,
    skip
  });

  useEffect(() => {
    if (!phase) {
      return;
    }

    if (!skip) {
      setIdeasQueryVariables({
        challengeSelection: challenge,
        phaseSelection: phase,
        filters: { userFields },
        order: sort,
        offset: offset * 10
      });
    }
  }, [challenge, phase, userFields, setIdeasQueryVariables, skip, sort, offset]);

  const ideasLabel = intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' });

  const chartSeries = useMemo(() => {
    const phases = chartData?.challenge?.phases || [];
    return [
      {
        id: ideasLabel,
        data: phases.map((phase) => {
          const name = phase.name || intl.formatMessage({ id: 'unnamedPhase', defaultMessage: 'Unnamed phase' });

          return {
            x: name,
            y: phase?.ideas?.length ?? 0,
            phaseId: Number(phase.id),
            startDate: phase.startDate,
            endDate: phase.endDate
          };
        })
      }
    ];
  }, [chartData?.challenge?.phases, ideasLabel, intl]);

  const sortIdeas = (column: string) => {
    const [sortColumn, sortDirection] = sort[0];
    const newOrder = [
      [
        column,
        column === sortColumn && sortDirection === CoreApiSortDirectionType.ASC
          ? CoreApiSortDirectionType.DESC
          : CoreApiSortDirectionType.ASC
      ]
    ];
    setSort(newOrder);
    setIdeasQueryVariables({
      ...ideasQueryVariables,
      order: newOrder
    });
  };

  const getColumnSortDirection = (column: string) => {
    const [sortColumn, sortDirection] = sort[0];
    return sortColumn === column
      ? sortDirection === CoreApiSortDirectionType.DESC
        ? AlchemySortDirectionType.DESC
        : AlchemySortDirectionType.ASC
      : 'none';
  };

  const windowWidth = useWindowWidth();
  const chartScrollWidth = useMemo(() => getChartScrollWidth(windowWidth), [windowWidth]);
  const chartMinWidth = useMemo(() => getChartMinWidth(chartSeries[0]?.data.length ?? 0), [chartSeries]);

  const challenges = funnelChallengeList?.challenges || [];
  const ideasDataCount = ideasData?.ideasAndCount?.count;
  const formatDate = (date: Date) => date.toISOString().slice(0, 10);
  const tableColumns = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'phase', defaultMessage: 'Phase' }) },
      { label: intl.formatMessage({ id: 'ideaCount', defaultMessage: 'Idea Count' }) }
    ],
    [intl]
  );

  const buildTableRows = useCallback(() => {
    return (
      chartData?.challenge?.phases?.map((phase) => [
        phase.name || intl.formatMessage({ id: 'unnamedPhase', defaultMessage: 'Unnamed phase' }),
        phase?.ideas?.length ?? 0
      ]) || []
    );
  }, [chartData?.challenge?.phases, intl]);

  const tableRows = useMemo(() => {
    if (!dataTableViewEnabled) {
      return [];
    }

    return buildTableRows();
  }, [buildTableRows, dataTableViewEnabled]);

  const getExportData = useCallback(() => {
    return { columns: tableColumns, rows: buildTableRows() };
  }, [buildTableRows, tableColumns]);

  if (loadingFunnelChallenges && !funnelChallengeList) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator inline />
      </Styled.LoadingContainer>
    );
  }

  return (
    <Grid key="ideasByPhaseStats">
      <CustomFilters
        userFieldOptions={userFieldOptions}
        communityUserFields={communityUserFields}
        countries={countries}
        userFieldUpdate={setUserFieldOptions}
        setSkip={setSkip}
      />
      <GridHeader key="challengeSelectionHeader">
        {intl.formatMessage({ id: 'show', defaultMessage: 'Show' })}
      </GridHeader>
      <GridRow key="challengeSelection">
        <Select
          defaultValue={challenge}
          options={[
            {
              label: intl.formatMessage({ id: 'selectAChallenge', defaultMessage: 'Select a challenge' }),
              value: ChallengeSelectionType.SELECT,
              'data-testid': `challege-${ChallengeSelectionType.SELECT}`
            },
            ...challenges.map((challenge) => ({
              label: challenge.title,
              value: challenge.id,
              'data-testid': `challege-${challenge.title}`
            }))
          ]}
          onChange={(challengeSelection) => {
            setChallenge(challengeSelection.value);
          }}
        />
      </GridRow>
      <GridRow key="updateButton">
        <Button
          priority="secondary"
          onClick={() => {
            getChallenge({ variables: { id: challenge } });
            getFilteredUserIds({ variables: { filters: { userFields } } });
          }}
        >
          {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
        </Button>
      </GridRow>
      {challenge !== ChallengeSelectionType.SELECT && chartData && !dataTableViewEnabled && (
        <ChartActionsRow key="ideasByPhaseExport">
          <ChartExportMenu
            chartRef={chartContainerRef}
            fileName={intl.formatMessage({ id: 'ideasByPhase', defaultMessage: 'Ideas by Phase' })}
            formatDate={formatDate}
            getExportData={getExportData}
          />
        </ChartActionsRow>
      )}
      <GridRow key="chart">
        {challenge !== ChallengeSelectionType.SELECT &&
          chartData &&
          (dataTableViewEnabled ? (
            <TableView
              columns={tableColumns}
              rows={tableRows}
              formatDate={formatDate}
              onRowClick={(index: number) => {
                const phases = chartData?.challenge?.phases;
                if (phases && phases[index]) {
                  setPhase(Number(phases[index].id));
                  setSkip(false);
                  setOffset(0);
                }
              }}
              summary={intl.formatMessage({
                id: 'ideasByPhase',
                defaultMessage: 'Ideas by Phase'
              })}
            />
          ) : (
            <ChartScroll width={chartScrollWidth}>
              <ChartScrollInner minWidth={chartMinWidth} ref={chartContainerRef}>
                <ThemeProvider theme={accessibleChartTheme}>
                  <BarChart
                    axisX={{ label: intl.formatMessage({ id: 'phase', defaultMessage: 'Phase' }) }}
                    axisY={{
                      formatScreenReaderValue: (value) => {
                        return `${ideasLabel}: ${value}`;
                      },
                      label: intl.formatMessage({ id: 'ideaCount', defaultMessage: 'Idea Count' }),
                      formatValue: (value) => {
                        const numericValue = Number(value);
                        return Number.isInteger(numericValue) ? numericValue : '';
                      }
                    }}
                    data={chartSeries}
                    domainScaling="from-zero"
                    hasLegend={false}
                    hasValueLabels
                    onDataClick={(dataPoint) => {
                      if (typeof dataPoint.phaseId !== 'number') {
                        return;
                      }

                      setPhase(dataPoint.phaseId);
                      setSkip(false);
                      setOffset(0);
                    }}
                  />
                </ThemeProvider>
              </ChartScrollInner>
            </ChartScroll>
          ))}
      </GridRow>
      {challenge && ideasDataCount && ideasDataCount > 0 ? (
        <>
          <GridRow key="table" justify="center">
            <GridCol>
              <IdeasTable
                getColumnSortDirection={getColumnSortDirection}
                ideasAndCount={ideasData?.ideasAndCount || { count: 0, rows: [] }}
                intl={intl}
                sortIdeas={sortIdeas}
              />
            </GridCol>
            <GridCol>
              <Pagination
                numPages={Math.ceil((ideasDataCount || 1) / IDEAS_PER_PAGE)}
                activeIndex={offset}
                onPageClick={(page) => {
                  setSkip(false);
                  setOffset(page.index as number);
                }}
              />
            </GridCol>
          </GridRow>
          <GridRow key="exportData">
            <Button
              priority="secondary"
              onClick={() => {
                const exportUrl = new URL(EXPORT_TO_CSV_ENDPOINT + challenge.toString(), `https://${community.url}`);

                if (phase) {
                  exportUrl.searchParams.append('forPhase', phase.toString());
                }

                exportUrl.searchParams.append('includeComments', '0');

                if (Object.keys(userFieldOptions).length > 0) {
                  exportUrl.searchParams.append(
                    'customFiltersUserIds',
                    (customFiltersUserIds?.filteredUsers || []).toString()
                  );
                }

                window.location = exportUrl as unknown as Location;
              }}
            >
              {intl.formatMessage({ id: 'exportData', defaultMessage: 'Export Data' })}
            </Button>
          </GridRow>
        </>
      ) : (
        challenge &&
        ideasDataCount === 0 && (
          <ErrorMessage
            message={intl.formatMessage({ id: 'noIdeasWereFound', defaultMessage: 'No ideas were found' })}
            details={intl.formatMessage({
              id: 'pleaseAdjustYourSearch',
              defaultMessage: 'Please adjust your search parameters and try again.'
            })}
          />
        )
      )}
    </Grid>
  );
};
